import React from 'react';
import { connect } from 'react-redux';
import {
	Box,
	ChoiceButton,
	DebitCardIllustration,
	Flex,
	FormikTextInput,
	FormWrapper,
	Layout,
	MoneyQuestionIllustration,
	PrimaryButton,
	TertiaryButton,
	Text,
} from '@stashinvest/ui';
import { Form, Formik } from 'formik';
import {
	SignUpWrapper,
	SignUpWrapperInner,
} from 'stash/components/sign-up/SignUpScreenLayout';
import { BankErrorScreen, SkipFundingScreen } from './BankErrorScreen';
import { trackDepositSetupSubmittedEvent } from '../../debit-funding/events/segment';
import { getTransferPrefill, initiateTransfer } from '../../../actions';
import {
	accountCreatedFundEnteredAmount,
	accountCreatedFundPrefill1,
	accountCreatedFundPrefill2,
	accountCreatedFundPrefill3,
	accountCreatedFundScreenView,
	accountCreatedFundSecondaryCta,
	debitSignUpAmountBlur,
	debitSignUpTransferFunds,
	debitSignUpTransferFundsCta,
	noToDebitFundingCta,
	noToDebitFundingScreenView,
	noToDebitFundingSecondaryCta,
} from 'stash/actions/debitSignUpTrackingEvents';

import { stringToNumberConverter } from 'stash/utils/formatters';
import * as Yup from 'yup';
import { Origin } from '@stashinvest/product-events-javascript/lib/enum_properties';

let timeout;

const retry = (dispatch, fn, retries = 0, ms = 2000, maxRetries = 5) => {
	return new Promise((resolve, reject) => {
		return dispatch(fn())
			.then(resolve)
			.catch((e) => {
				timeout = setTimeout(() => {
					console.log('retrying failed promise...');
					++retries;
					if (retries >= maxRetries) {
						return reject(e);
					}
					retry(dispatch, fn, retries).then(resolve, reject);
				}, ms);
			});
	});
};

const mapDispatchToProps = (dispatch) => ({
	getTransferPrefill: () => {
		return retry(dispatch, () => getTransferPrefill('debitEnrollment'));
	},
	initiateTransfer: (data) => {
		return dispatch(initiateTransfer(data));
	},
	accountCreatedFundScreenView: () => {
		return dispatch(accountCreatedFundScreenView());
	},
	accountCreatedFundPrefill: (idx) => {
		switch (idx) {
			case 0:
				dispatch(accountCreatedFundPrefill1());
				break;
			case 1:
				dispatch(accountCreatedFundPrefill2());
				break;
			case 2:
				dispatch(accountCreatedFundPrefill3());
				break;
			default:
				break;
		}
	},
	accountCreatedFundEnteredAmount: () => {
		return dispatch(accountCreatedFundEnteredAmount());
	},
	accountCreatedFundSecondaryCta: () => {
		return dispatch(accountCreatedFundSecondaryCta());
	},
	noToDebitFundingScreenView: () => {
		return dispatch(noToDebitFundingScreenView());
	},
	noToDebitFundingCta: () => {
		return dispatch(noToDebitFundingCta());
	},
	noToDebitFundingSecondaryCta: () => {
		return dispatch(noToDebitFundingSecondaryCta());
	},
	debitSignUpTransferFunds: () => {
		return dispatch(debitSignUpTransferFunds());
	},
	debitSignUpTransferFundsCta: () => {
		return dispatch(debitSignUpTransferFundsCta());
	},
	debitSignUpAmountBlur: (amount) => {
		return dispatch(debitSignUpAmountBlur(amount));
	},
});

const mapStateToProps = (state) => ({
	prefillTransfer: state.entities.transfers.prefillTransfer,
});

export const validateTransferFundsUnder1 = (val) => {
	if (!val) return true;
	return stringToNumberConverter(val) >= 1;
};

export const validateTransferFundsOver500 = (val) => {
	if (!val) return true;
	return stringToNumberConverter(val) <= 500;
};

const validationSchema = Yup.object().shape({
	amount: Yup.string()
		.required('This field is required')
		.test(
			'min',
			'Please enter at least $1.00 for this transfer',
			validateTransferFundsUnder1
		)
		.test(
			'max',
			'This exceeds the transfer limit between these accounts',
			validateTransferFundsOver500
		),
});

const ButtonWrapper = (props, classProps) => (amt, idx) =>
	(
		<ChoiceButton
			key={amt}
			type="button"
			selected={props.values.amount === amt}
			onClick={() => {
				props.setValues({ amount: amt });
				classProps.accountCreatedFundPrefill(idx);
			}}
		>
			{amt}
		</ChoiceButton>
	);

export class DebitSignUpTransferFunds extends React.Component {
	constructor() {
		super();
		this.state = {
			transferScreen: true,
			loading: true,
			error: '',
		};
		this.detectScreenToShow = this.detectScreenToShow.bind(this);
	}

	componentDidMount() {
		this.props.accountCreatedFundScreenView();
		this.props
			.getTransferPrefill('debitEnrollment')
			.then(() => {
				this.setState({ loading: false });
			})
			.catch((err) => {
				this.setState({
					loading: false,
					transferScreen: true,
					error: err.errors[0].message,
				});
			});
	}

	componentWillUnmount() {
		if (typeof timeout !== undefined) clearTimeout(timeout);
	}

	transferScreen(prefillTransfer) {
		return (
			<SignUpWrapper>
				<DebitCardIllustration />
				<Text.H1 size="3" mb="xs">
					Alright! Time to transfer cash so we can ship your card.
				</Text.H1>
				<SignUpWrapperInner>
					<Text.Title mt="m">Transfer from</Text.Title>
					<Text>{prefillTransfer && prefillTransfer.source.name}</Text>

					<Box mb="xl">
						<Text.Title mt="m">Transfer to</Text.Title>
						<Text>{prefillTransfer && prefillTransfer.destination.name}</Text>
						<Text>(your new debit account)</Text>
					</Box>
					<Text.Title mt="m">Choose an amount to deposit:</Text.Title>
					<Formik
						initialValues={{
							amount: '$100',
						}}
						validationSchema={validationSchema}
						onSubmit={({ amount }) => {
							this.setState({ isLoading: true });
							trackDepositSetupSubmittedEvent(amount, Origin?.bankOnboarding);
							this.props
								.initiateTransfer({
									...prefillTransfer,
									amount: {
										value: amount,
										currency: 'USD',
									},
								})
								.then(() => {
									this.setState({ isLoading: false });
									this.props.history.push('/debit-download');
								});
						}}
					>
						{(props) => {
							return (
								<Form>
									<FormWrapper>
										{['$50', '$100', '$150'].map(ButtonWrapper(props, this.props))}
										<Text.Title mt="m" mb="s">
											Or enter an amount:
										</Text.Title>
										<FormikTextInput
											name="amount"
											mask="currency"
											label="Transfer amount"
											placeholder="$0.00"
											maxLength="6"
											onFocus={debitSignUpAmountBlur}
										/>
										<PrimaryButton type="submit" loading={this.state.isLoading} mb="xs">
											Confirm {props.values.amount} transfer
										</PrimaryButton>
										<Flex justifyContent={'center'}>
											<TertiaryButton
												onClick={(e) => {
													e.preventDefault();
													this.props.accountCreatedFundSecondaryCta();
													this.setState({
														transferScreen: false,
													});
												}}
											>
												I don’t want my card
											</TertiaryButton>
										</Flex>
									</FormWrapper>
								</Form>
							);
						}}
					</Formik>
				</SignUpWrapperInner>
			</SignUpWrapper>
		);
	}

	detectScreenToShow() {
		const { prefillTransfer, debitSignUpTransferFundsCta, debitSignUpTransferFunds } =
			this.props;

		if (this.state.loading === true) {
			return <Layout.PageWrapLoader />;
		}
		if (this.state.error) {
			return (
				<BankErrorScreen
					onMount={debitSignUpTransferFunds}
					graphic={MoneyQuestionIllustration}
					header={"Looks like your bank account isn't set up yet."}
					subheader={'Check back in a few days.'}
					cta={'Back to Home'}
					ctaLink={'/'}
					ctaCallback={debitSignUpTransferFundsCta}
				/>
			);
		}

		return this.transferScreen(prefillTransfer);
	}

	render() {
		const {
			noToDebitFundingScreenView,
			noToDebitFundingCta,
			noToDebitFundingSecondaryCta,
		} = this.props;

		return (
			<div>
				{this.state.transferScreen === true ? (
					this.detectScreenToShow()
				) : (
					<SkipFundingScreen
						onMount={noToDebitFundingScreenView}
						ctaCallback={() => {
							noToDebitFundingCta();
							this.setState({
								transferScreen: true,
							});
						}}
						secondaryCtaCallback={() => {
							noToDebitFundingSecondaryCta();
							this.setState({
								transferScreen: false,
							});
						}}
					/>
				)}
			</div>
		);
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(DebitSignUpTransferFunds);
